import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Alert, Button, RadioPresets, SectionCard, PageWrapper } from "@/deps/ui-library";
import { useOpenDemo } from "@/api/queries";
import { PageHeader } from "@/components/PageHeader";

type Start = "fresh" | "demo";

/**
 * "How do you want to start?" (the design's trial path, plan slice 10.8), right after signing up. Start fresh runs the
 * company wizard on the person's own, empty books. Explore with demo data opens a separate sandbox company full of
 * sample books (ADR-0063); their own company stays as it is, in the company switcher, for when they're ready.
 */
export function StartPage() {
  const [choice, setChoice] = useState<Start>("fresh");
  const demo = useOpenDemo();
  const navigate = useNavigate();

  const go = () => {
    if (choice === "fresh") {
      void navigate({ to: "/setup" });
      return;
    }
    demo.mutate(undefined, { onSuccess: () => void navigate({ to: "/" }) });
  };

  return (
    <PageWrapper spacing="md" className="p-0">
      <PageHeader
        title="How do you want to start?"
        subtitle="Either way, your free trial has begun. You can switch between the two later."
      />
      <SectionCard title="Start">
        <div className="flex flex-col gap-4">
          <RadioPresets.CardRadio
            aria-label="How to start"
            value={choice}
            onValueChange={(details: { value: string | null }) => {
              if (details.value === "fresh" || details.value === "demo") setChoice(details.value);
            }}
            items={[
              {
                value: "fresh",
                label: "Start fresh",
                description:
                  "Your own company with empty books. A short setup asks for your tax pack, chart of accounts, bank accounts and numbering.",
              },
              {
                value: "demo",
                label: "Explore with demo data",
                description:
                  "A separate sample company, Brightwater Consulting, with last year's and this year's books. Try anything: its emails are simulated and nothing moves to your own books.",
              },
            ]}
          />
          {demo.isError ? (
            <Alert
              config={{ variant: "subtle", colorScheme: "danger" }}
              description={
                demo.error instanceof Error
                  ? demo.error.message
                  : "The demo company couldn't be set up. Try again."
              }
            />
          ) : null}
          <div className="flex justify-end">
            <Button colorScheme="primary" size="sm" loading={demo.isPending} onClick={go}>
              {choice === "fresh" ? "Set up my company" : "Open the demo company"}
            </Button>
          </div>
          {demo.isPending ? (
            <p className="text-fg-muted m-0 text-right text-xs">
              Filling the demo company with a year and a half of sample books. This takes a few
              seconds.
            </p>
          ) : null}
        </div>
      </SectionCard>
    </PageWrapper>
  );
}
